// v48b — stars saved to the account. One shared store per scope, so the
// X Creators list and an X profile always show the same star.
// Scopes: "grid" | "x" | "pipeline:<pid>" (each fully separate).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const store = new Map();
const subs = new Map();

function emit(scope) {
  (subs.get(scope) || new Set()).forEach((fn) => fn(store.get(scope) || new Set()));
}

export function setStars(scope, ids) {
  store.set(scope, new Set(ids));
  emit(scope);
}

export function resetStars() {
  store.clear();
}

export async function loadStars(scope) {
  try {
    const { data } = await api.get("/stars", { params: { scope } });
    setStars(scope, data?.ids || []);
  } catch (_) { /* keep what we have */ }
  return store.get(scope) || new Set();
}

export function useStars(scope) {
  const [ids, setIds] = useState(() => (scope && store.get(scope)) || new Set());
  const [ready, setReady] = useState(false);
  // Bumps once the server has saved a star change (drives server re-sorts).
  const [savedVersion, setSavedVersion] = useState(0);

  useEffect(() => {
    if (!scope) return undefined;
    setIds(store.get(scope) || new Set());
    setReady(false);
    const fn = (s) => setIds(new Set(s));
    if (!subs.has(scope)) subs.set(scope, new Set());
    subs.get(scope).add(fn);
    loadStars(scope).then(() => setReady(true));
    return () => { subs.get(scope)?.delete(fn); };
  }, [scope]);

  const toggle = useCallback(async (id) => {
    if (!scope || !id) return;
    const prev = store.get(scope) || new Set();
    const on = !prev.has(id);
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    setStars(scope, next);
    const path = `/stars/${encodeURIComponent(scope)}/${encodeURIComponent(id)}`;
    try {
      if (on) await api.put(path); else await api.delete(path);
      setSavedVersion((v) => v + 1);
    } catch (_) {
      const back = new Set(store.get(scope) || []);
      if (on) back.delete(id); else back.add(id);
      setStars(scope, back);
      toast.error("Could not save the star. Try again.");
    }
  }, [scope]);

  const has = useCallback((id) => ids.has(id), [ids]);
  return { ids, has, toggle, count: ids.size, ready, savedVersion };
}
